import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** Floating 3D coin — Rupee on the front face, Tether (₮) on the back. Pure CSS 3D. */
export function VaultCoin({ size = 180, paused = false, className }) {
  return (
    <div className={cn("coin-scene", className)} style={{ width: size, height: size }} aria-hidden="true">
      <div className={cn("coin h-full w-full", paused && "paused")} data-testid="vault-coin">
        <div className="face front" style={{ fontSize: size * 0.42 }}>₹</div>
        <div className="edge" />
        <div className="face back" style={{ fontSize: size * 0.42 }}>₮</div>
      </div>
    </div>
  );
}

/** A premium glass panel that tilts in response to the pointer. */
export function TiltCard({ children, className, strength = 10, testId }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (0.5 - py) * strength;
    const ry = (px - 0.5) * strength;
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
    setStyle({ transform: `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)` });
  };
  const reset = () => setStyle({ transform: "perspective(900px) rotateX(0deg) rotateY(0deg)" });

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className={cn("tilt-card relative", className)}
      style={style}
      data-testid={testId}
    >
      <div className="tilt-inner relative">{children}</div>
      <span className="shine" />
    </div>
  );
}

/** Faint drifting sparkles across the vault. */
export function VaultSparkles({ count = 14 }) {
  const [dots] = useState(() =>
    Array.from({ length: count }).map((_, i) => ({
      left: `${(i * 97) % 100}%`,
      delay: `${(i * 0.9) % 6}s`,
      duration: `${5 + ((i * 1.4) % 5)}s`,
      top: `${50 + ((i * 23) % 50)}%`,
    }))
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d, i) => (
        <span
          key={i}
          className="vault-sparkle"
          style={{ left: d.left, top: d.top, animationDelay: d.delay, animationDuration: d.duration }}
        />
      ))}
    </div>
  );
}

/** Odometer-style number that animates when it changes. */
export function CountUp({ value, duration = 500, format = (v) => v.toFixed(2), className, testId }) {
  const [display, setDisplay] = useState(Number(value) || 0);
  const start = useRef({ from: 0, at: 0 });
  useEffect(() => {
    const to = Number(value) || 0;
    start.current = { from: display, at: performance.now() };
    let raf;
    const step = (t) => {
      const elapsed = t - start.current.at;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start.current.from + (to - start.current.from) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className={cn("num", className)} data-testid={testId}>{format(display)}</span>;
}
